import type { CurrencyType } from '@/core/tamagoshi/Price/Price';
import type { AvailablePaymentMethodName } from '@/customerFinanceOperations/interfaces/PaymentMethods';
import type { countryCode } from '@/customerManagement/interfaces/countryCode';
import type {
  DeliveryExpressMode,
  DeliveryMode,
} from '@/productDelivery/DeliveryPromisesList/constants';
import type { FunnelFlow } from '@/shoppingCart/services/msfCheckoutFunnel/interfaces/gateway';

export interface DeliveryOptionsRetrieval {
  flow: FunnelFlow;
  customerOrder: CustomerOrder<DeliveryFunnelPrice>;
  delivery: Delivery;
  contact: FunnelContact;
  billing: { address: FunnelCustomerAddress };
  paymentOptions: { overview: FunnelPaymentOptionOverview[] };
  crossBorderVat?: CrossBorderVat;
}

export interface DeliveryPickupPointsLookup {
  flow: FunnelFlow;
  pickupPoints: DeliveryPickupPointLookup[];
}

export interface DeliveryPickupPointLookup {
  id: string;
  name: string;
  distance: number;
  location: Location;
  openingHours: PickUpPointOpeningHour[];
}

export enum CrossBorderCode {
  VATB2C = 'VATB2C',
  VATB2B_FRAUD = 'VATB2B_FRAUD',
  VATB2B_NOT_SUBJECT_TO_VAT = 'VATB2B_NOT_SUBJECT_TO_VAT',
  VATB2B_NONGB_LOCAL = 'VATB2B_NONGB_LOCAL',
  VATB2B_NONGB_INTRACO_INVALID = 'VATB2B_NONGB_INTRACO_INVALID',
  VATB2B_RC_INVALID = 'VATB2B_RC_INVALID',
  VATB2B_GB_LOCAL = 'VATB2B_GB_LOCAL',
  VATB2B_NI_LOCAL = 'VATB2B_NI_LOCAL',
  VATB2B_NI_INTRACO_INVALID = 'VATB2B_NI_INTRACO_INVALID',
  VATB2B_NONGB_INTRACO_VALID = 'VATB2B_NONGB_INTRACO_VALID',
  VATB2B_RC_VALID = 'VATB2B_RC_VALID',
  VATB2B_ESIT_IMPORT_VALID = 'VATB2B_ESIT_IMPORT_VALID',
  VATB2B_FRDE_IMPORT_VALID = 'VATB2B_FRDE_IMPORT_VALID',
  VATB2B_GB_IMPORT_VALID_INF135 = 'VATB2B_GB_IMPORT_VALID_INF135',
  VATB2B_GB_IMPORT_VALID_SUP135 = 'VATB2B_GB_IMPORT_VALID_SUP135',
  VATB2B_NI_INTRACO_VALID = 'VATB2B_NI_INTRACO_VALID',
  VATB2B_NI_IMPORT_VALID = 'VATB2B_NI_IMPORT_VALID',
  VATB2B_UNKNOWN_SHIPFROM = 'VATB2B_UNKNOWN_SHIPFROM',
  VATB2B_ALL_IMPORT_INVALID = 'VATB2B_ALL_IMPORT_INVALID',
  VATB2B_FRDE_IMPORT_INVALID = 'VATB2B_FRDE_IMPORT_INVALID',
  VATB2B_GB_IMPORT_INVALID = 'VATB2B_GB_IMPORT_INVALID',
  VATB2B_NI_IMPORT_INVALID = 'VATB2B_NI_IMPORT_INVALID',
  X_BORDER_NOT_ACTIVATED = 'X_BORDER_NOT_ACTIVATED',
  VATB2B_BELGIUM = 'VATB2B_BELGIUM',
}

interface CrossBorderItem {
  itemKey: string;
  itemType: string;
  statusCode: CrossBorderCode;
}

interface CrossBorderUser {
  subjectedToVat: string;
  localVatStatus: string;
  localVatNumber: string;
  intracomVatStatus: string;
  intracomVatNumber: string;
  validationProofDocumentName: string;
}
export interface CrossBorderVat {
  user?: CrossBorderUser;
  items: CrossBorderItem[];
}

interface Location {
  address: Address;
  geoCoordinates: GeoCoordinates;
}

interface GeoCoordinates {
  latitude: number;
  longitude: number;
}

export interface Address {
  street: string;
  postalCode: string;
  city: string;
  countryIsoCode: countryCode;
}

interface PickUpPointOpeningHour {
  day: string;
  hours: string;
  isOpen: boolean;
}

export interface CustomerOrder<T = FunnelPrice> {
  id: string;
  reference: string;
  expiredAt: string;
  status: CustomerOrderStatus;
  totalCouponPriceVatExcl: T;
  totalCouponPriceVatIncl: T;
  totalDeliveryDiscountPriceVatExcl: T;
  totalDeliveryDiscountPriceVatIncl: T;
  totalPriceToPayByCustomerVatExcl: T;
  totalPriceToPayByCustomerVatIncl: T;
  totalProductPriceVatExcl: T;
  totalProductPriceVatIncl: T;
  totalShippingPriceVatExcl: T;
  totalShippingPriceVatIncl: T;
  totalShippingPriceToPayByCustomerVatIncl: T;
  totalShippingPriceToPayByCustomerVATExcl: T;
  coupons: FunnelCoupon[] | null;
  items: FunnelProduct[];
}

export interface DeliveryFunnelProduct extends Omit<FunnelProduct, 'price'> {
  price: {
    currency: CurrencyType;
    inclVat: number | null;
    exclVat: number | null;
  };
}

export interface FunnelPrice {
  currency: CurrencyType;
  amount: number;
}
export interface DeliveryFunnelPrice extends Omit<FunnelPrice, 'amount'> {
  amount: number | null;
}

export interface FunnelCoupon {
  code: string;
  value: string;
  details: {
    isPercent: boolean;
    maxAbsoluteValue?: string;
    maxAbsoluteValueReached?: boolean;
  };
  initiative: string;
}

export interface Price {
  currency: CurrencyType;
  inclVat: number;
  exclVat: number;
}
export interface FunnelProduct {
  id: string;
  modelId: string;
  offerId: string;
  weight: number;
  quantity: number;
  name: string;
  picture: string;
  productId: string;
  price: Price;
  sku: string;
  isMmf: boolean;
  seller: {
    id: string;
    name: string;
    isForeigner: boolean;
  };
}

export type CustomerOrderStatus =
  | 'CREATED'
  | 'PENDING'
  | 'ACCEPTED'
  | 'READY'
  | 'VALID'
  | 'CANCELED';

export interface Delivery {
  message?: string;
  address: FunnelCustomerAddress;
  isAddressDeliverable: boolean;
  dispatches: DeliveryDispatch[];
  discount?: Discount;
}

export interface Discount {
  totalSavings: FunnelPrice;
  typeId: number;
  condition: {
    [key: string]: string;
  };
}

export interface FunnelCustomerAddress {
  id: number;
  company: string;
  firstname: string;
  lastname: string;
  latitude: string;
  longitude: string;
  phoneNumber: string;
  street: string;
  postalCode: string;
  city: string;
  countryIsoCode: countryCode;
  complete: boolean;
}

export interface DeliveryDispatch {
  id: string;
  manoFulfillment: boolean;
  items: number[];
  deliveryPromises: DeliveryPromise[] | null;
}

export interface DeliveryPromise {
  id: string;
  label: string;
  slug?: string;
  mode: DeliveryMode;
  period: DeliveryPromisePeriod;
  priceVatIncluded: FunnelPrice;
  priceVatExcluded?: FunnelPrice;
  window: {
    max: number;
    min: number;
  };
  isSelected: boolean;
  isRecommended: boolean;
  origin: string;
  information: {
    externalCarrierName: string;
    externalCarrierOffer: string;
    externalDeliveryMethod: string;
  };
  pickUpPoint?: DeliveryPickUpPoint;
  slot?: DeliveryScheduleSlot;
  discountedPriceVatIncluded?: FunnelPrice;
  discountedPriceVatExcluded?: FunnelPrice;
}

export interface DeliveryPromisePeriod {
  cutoff: string;
  isExpress: boolean;
  expressMode: DeliveryExpressMode;
  max: string;
  min: string;
}

export interface FunnelContact {
  phoneCall: {
    phoneNumber: string;
    price: number | string; // In case the received value from the ms is free, the value should be 0
  };
  availabilities: {
    monday: FunnelContactAvailabilitySlot[];
    tuesday: FunnelContactAvailabilitySlot[];
    wednesday: FunnelContactAvailabilitySlot[];
    thursday: FunnelContactAvailabilitySlot[];
    friday: FunnelContactAvailabilitySlot[];
    saturday: FunnelContactAvailabilitySlot[];
    sunday: FunnelContactAvailabilitySlot[];
  };
}

interface FunnelContactAvailabilitySlot {
  start: string;
  end: string;
}

export interface FunnelPaymentOptionOverview {
  name: AvailablePaymentMethodName;
  isEnabled: boolean;
}

export interface CheckoutFunnelIds {
  cartId?: string;
  customerOrderId?: string;
}

export interface PickupPoint {
  pickupPointSlug: string;
  address: Address;
  geoCoordinates: GeoCoordinates;
}

export interface DeliveryScheduleSlot {
  dateStart: string;
  dateEnd: string;
}

export interface DeliveryPickUpPoint {
  id: string;
  name: string;
  street: string;
  postalCode: string;
  city: string;
  countryIsoCode: countryCode;
  country: string;
  openingHours?: {
    day: string;
    hours: string;
    isOpen: boolean;
  }[];
}

export interface DeliveryShippingMethodSelection {
  flow: FunnelFlow;
  customerOrder: CustomerOrder<DeliveryFunnelPrice>;
  delivery: {
    dispatches: DeliveryDispatch[];
    discount?: Discount;
  };
  paymentOptions: {
    overview: FunnelPaymentOptionOverview[];
  };
}

export interface DeliveryScheduleSlotsLookUp {
  promiseId: string;
  scheduleSlots: DeliveryScheduleSlot[];
}

export interface DeliveryFinalizationPayload {
  dispatchId: string;
  deliveryPromiseId: string;
  slot?: DeliveryScheduleSlot;
  pickUpPoint?: DeliveryPickUpPoint;
}

export interface DeliveryFinalizationResponse {
  customerOrder: {
    id: string;
    reference: string;
    expiredAt: string;
    status: string;
  };
  delivery: {
    promiseId: string;
    dispatchId: string;
    slot?: DeliveryScheduleSlot;
    pickUpPoint?: DeliveryPickUpPoint;
  }[];
  flow: FunnelFlow;
}
